import type { CategoryKey } from './categories';

export * from './categories';

/** Category key → percentage. Keys must be valid CategoryKey values. */
export type Allocations = Partial<Record<CategoryKey, number>>;

/**
 * Portal testimonial copy limits, derived from what the slider can show rather than picked by feel.
 *
 * Mobile is the binding case: the quote is clamped to six lines over the photo, because anything
 * taller buries the image the testimonial is meant to illustrate (TEC-3641). Measured on the
 * rendered slide at its 1.25rem inset, across five real Spanish testimonials, six lines hold:
 *
 *   320px viewport → 205-221 chars   375px → 262-273
 *   360px viewport → 248-262 chars   390px → 262-285
 *
 * The spread within a column is word-wrap luck, so the cap has to clear the *worst* case, not the
 * average. 240 sits under the 360px floor of 248, which means real copy fits whole on every phone
 * from 360px up; a 320px screen (iPhone SE 1) loses a line or two to the clamp. The author line is
 * one line from 414px, two at 360px, and the slider clamps it at three.
 *
 * These are hard caps on save, not hints: the slider truncates anything longer, so characters over
 * the limit are characters nobody reads.
 */
export const testimonialQuoteMaxLength = 240;
export const testimonialAuthorMaxLength = 60;

/**
 * Length of a rich-text value as a reader sees it. The NGO portal form edits the quote in a rich
 * text editor, so the stored value is HTML and its raw `.length` counts markup the slide never
 * shows — `<p></p>` alone is seven characters of a 240 budget.
 *
 * The `CharacterCount` component counts with this same function on purpose. If the counter and the
 * validator ever disagreed, a field would read 240/240 in green and then fail to save.
 */
export function plainTextLength(value: string): number {
	return value.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').length;
}

export const languages = {
	Spanish: 'SPA',
	English: 'ENG',
	Catalan: 'CAT',
	Euskera: 'EUS',
	Gallego: 'GLG',
	German: 'GER',
	French: 'FRE',
	Korean: 'KOR',
	Chinese: 'CHI',
	Japanese: 'JPN',
	Portuguese: 'POR',
	Russian: 'RUS',
	Italian: 'ITA',
	Dutch: 'DUT',
	Greek: 'GRE',
} as const;

export type Language = (typeof languages)[keyof typeof languages];

export const locales = [
	'es',
	'en',
	'ca',
	'eu',
	'gl',
	'de',
	'fr',
	'ko',
	'zh',
	'ja',
	'pt',
	'ru',
	'it',
	'nl',
	'el',
] as const;

export type Locale = (typeof locales)[number];

export const localeToLanguage: Record<Locale, Language> = {
	es: languages.Spanish,
	en: languages.English,
	ca: languages.Catalan,
	eu: languages.Euskera,
	gl: languages.Gallego,
	de: languages.German,
	fr: languages.French,
	ko: languages.Korean,
	zh: languages.Chinese,
	ja: languages.Japanese,
	pt: languages.Portuguese,
	ru: languages.Russian,
	it: languages.Italian,
	nl: languages.Dutch,
	el: languages.Greek,
};

export function localeToLang(locale: string): Language | undefined {
	const normalizedLocale = locale.trim().toLowerCase();
	return localeToLanguage[normalizedLocale as keyof typeof localeToLanguage];
}

export enum merchantOrigin {
	backoffice,
	onboarding,
	onboardingMagento,
	onboardingPrestashop,
	onboardingTpv,
}

export const channelTypes = ['Corporate', 'Tpv', 'Widget', 'API', 'Custom'] as const;

export type ChannelType = (typeof channelTypes)[number];

/**
 * The one channel type that is a physical point of sale — a terminal in a shop. Every other member
 * is an online channel: `Corporate` is a donor portal, `Widget` an embedded form, `API` a merchant's
 * own integration, `Custom` everything else.
 *
 * Two vocabularies name this concept, and only this one reaches a donation. The core channels table
 * discriminates its rows with `'store' | 'pointOfSale' | 'ecommerce'`; a donation carries the
 * application it came through, mapped onto `channelTypes` by BI's `getChannelType` (`tpv` → `Tpv`).
 * So anything counting points of sale from donation data matches on THIS constant, and `pointOfSale`
 * is the spelling of the same idea one table over.
 */
export const POINT_OF_SALE_CHANNEL_TYPE: ChannelType = 'Tpv';

export const WORLDCOO_MERCHANT_ID = '997d0641-65ab-4897-9719-e4977f733527';

// Routes Patagonia portal donations to the `thanks-donation-portal-patagonia` email template (VSNI-42).
export const PATAGONIA_MERCHANT_ID = 'fd9bfc6d-5853-491a-8413-06373db4be20';

/**
 * Ceiling for a donation amount, monthly for a subscription. Exclusive: an amount has to stay
 * strictly below it, which is why the copy reads "less than" and the checks use `>=`.
 *
 * Denominated in euros, so a check against it only holds while the amount being checked is in euros
 * too. The number is formatted per locale wherever it reaches a donor, so do not spell it out in a
 * comment or a message: `es` renders it `3000` and `en` renders it `3,000`.
 */
export const DONATION_AMOUNT_LIMIT = 3000;

export type DocumentType = 'id' | 'taxId' | 'passport' | 'nie' | 'others';

export const applicationTypes = [
	'merchantDefault',
	'api',
	'widget',
	'backOffice',
	'portal',
	'tpv',
	'recurringDonation',
	'file',
	'paymentWidget',
	'merchantImplementation',
	'extra',
	'shopify',
] as const;

export type ApplicationType = (typeof applicationTypes)[number];

export const commercialTracks = ['pre-plan-b', 'plan-b'] as const;
export type CommercialTrack = (typeof commercialTracks)[number];

/** NGOs without a declared commercial track are on the introductory pre-plan-b track. */
export function resolveCommercialTrack(commercialTrack: CommercialTrack | undefined): CommercialTrack {
	return commercialTrack ?? 'pre-plan-b';
}

/** The Worldcoo fee percentage an NGO pays, driven by its commercial track. */
export function getWorldcooFeeForTrack(commercialTrack: CommercialTrack): number {
	return commercialTrack === 'plan-b' ? 15 : 12;
}

export const commercialTrackLabels: Record<CommercialTrack, string> = {
	'plan-b': 'Plan B',
	'pre-plan-b': 'Pre-Plan B',
};

export const campaignStatuses = ['sketch', 'scheduled', 'active', 'finished', 'disabled'] as const;
export type CampaignStatus = (typeof campaignStatuses)[number];

export const campaignStatusLabels: Record<CampaignStatus, string> = {
	sketch: 'Sketch',
	scheduled: 'Scheduled',
	active: 'Active',
	finished: 'Finished',
	disabled: 'Disabled',
};

export const languageToLocale = {
	[languages.Spanish]: 'es',
	[languages.English]: 'en',
	[languages.Catalan]: 'ca',
	[languages.Euskera]: 'eu',
	[languages.Gallego]: 'gl',
	[languages.German]: 'de',
	[languages.French]: 'fr',
	[languages.Korean]: 'ko',
	[languages.Chinese]: 'zh',
	[languages.Japanese]: 'ja',
	[languages.Portuguese]: 'pt',
	[languages.Russian]: 'ru',
	[languages.Italian]: 'it',
	[languages.Dutch]: 'nl',
	[languages.Greek]: 'el',
} as const satisfies Record<Language, Locale>;
